import * as Sentry from "@sentry/react";

// Rate-limited triage logging for the @nudge-ai/audio-store local backup.
// Everything here is bounded so a wedged storage tier can't flood Sentry.

const lastAt = new Map<string, number>();
const THROTTLE_MS = 30_000;

function throttled(key: string, fn: () => void): void {
  const now = Date.now();
  if (now - (lastAt.get(key) ?? 0) < THROTTLE_MS) return;
  lastAt.set(key, now);
  fn();
}

// Throttled to once per 30s per state+engine; error/blocked also warn to console.
export function logAudioStoreConnection(event: {
  state: string;
  engine?: string;
  reason?: string;
  error?: Error;
}): void {
  try {
    const key = `${event.state}:${event.engine ?? ""}`;
    throttled(key, () => {
      const level = event.state === "error" ? "error" : event.state === "blocked" ? "warning" : "info";
      Sentry.addBreadcrumb({
        category: "audio-store",
        level,
        message: `connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
        data: { reason: event.reason, error: event.error?.message },
      });
      if (level !== "info") {
        console.warn(
          `[audio-store] connection ${event.state}${event.engine ? ` (${event.engine})` : ""}`,
          event.reason ?? "",
        );
      }
    });
  } catch {
    // logging must never throw into a storage operation
  }
}

// Local save failed on every tier. Capped per audio_id so a wedged session can't flood.
const MAX_CAPTURES_PER_RECORDING = 3;
const MAX_TRACKED_RECORDINGS = 100;
const saveFailCounts = new Map<string, number>();

export function logAudioStoreSaveFailure(error: unknown, data: Record<string, unknown>): void {
  try {
    const key = String(data.audio_id ?? "unknown");
    // Bound the map so a long-lived tab can't leak.
    if (!saveFailCounts.has(key) && saveFailCounts.size >= MAX_TRACKED_RECORDINGS) {
      saveFailCounts.clear();
    }
    const count = (saveFailCounts.get(key) ?? 0) + 1;
    saveFailCounts.set(key, count);

    if (count <= MAX_CAPTURES_PER_RECORDING) {
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: { ...data, failCount: count, context: "audio-store local save failed on all tiers" },
      });
    } else if (count === MAX_CAPTURES_PER_RECORDING + 1) {
      Sentry.addBreadcrumb({
        category: "audio-store",
        level: "warning",
        message: "local save failing repeatedly; suppressing further captures for this recording",
        data,
      });
    }
  } catch {
    // logging must never throw into a storage operation
  }
}
